import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input } from '../components';
import { ShieldCheck, Smartphone, FileSignature } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchOvoContract, vraagTekencode, ondertekenContract } from '../services/projectenApi';

/**
 * Model-OvO viewer + SMS-2FA-ondertekening (stap 5 van het projecttraject).
 * Zelfde component voor beide partijen; de backend bepaalt wie er aan de
 * beurt is (WACHT_OP_WERKGEVER → WACHT_OP_PROFESSIONAL → GETEKEND).
 */

interface Props {
  contractId: string;
  /** 'werkgever' of 'professional' — alleen voor de knoptekst. */
  rol: 'werkgever' | 'professional';
  onGetekend?: () => void;
}

const OvoContractSign: React.FC<Props> = ({ contractId, rol, onGetekend }) => {
  const queryClient = useQueryClient();
  const [code, setCode] = React.useState('');
  const [codeVerstuurd, setCodeVerstuurd] = React.useState<string | null>(null);

  const { data: contract, isLoading } = useQuery({
    queryKey: ['ovo-contract', contractId],
    queryFn: async () => (await fetchOvoContract(contractId)).data?.data,
  });

  const codeMutation = useMutation({
    mutationFn: () => vraagTekencode(contractId),
    onSuccess: (res) => {
      setCodeVerstuurd(res.data?.data?.telefoon_hint ?? '');
      toast.success('SMS-code verstuurd');
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Versturen van de SMS-code is mislukt'),
  });

  const tekenMutation = useMutation({
    mutationFn: () => ondertekenContract(contractId, code.trim()),
    onSuccess: () => {
      toast.success('Handtekening geregistreerd');
      setCode('');
      setCodeVerstuurd(null);
      queryClient.invalidateQueries({ queryKey: ['ovo-contract', contractId] });
      onGetekend?.();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Ondertekenen is mislukt'),
  });

  if (isLoading || !contract) {
    return <p className="text-center text-gray-600 py-8">Contract laden…</p>;
  }

  const aanDeBeurt =
    (rol === 'werkgever' && contract.status === 'WACHT_OP_WERKGEVER') ||
    (rol === 'professional' && contract.status === 'WACHT_OP_PROFESSIONAL');

  return (
    <div className="space-y-4">
      <Card>
        <div
          className="p-6 prose prose-sm max-w-none [&_table]:w-full [&_td]:border [&_td]:p-1 [&_th]:border [&_th]:p-1"
          dangerouslySetInnerHTML={{ __html: contract.contract_html }}
        />
      </Card>

      <Card>
        <div className="p-6">
          {contract.status === 'GETEKEND' ? (
            <div className="flex items-start gap-3 text-green-800">
              <ShieldCheck className="w-6 h-6 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold">Contract dubbel ondertekend en cryptografisch verzegeld</p>
                <p>
                  Werkgever: {new Date(contract.werkgever_getekend_op).toLocaleString('nl-NL')} (IP{' '}
                  {contract.werkgever_teken_ip}) · Professional:{' '}
                  {new Date(contract.professional_getekend_op).toLocaleString('nl-NL')} (IP{' '}
                  {contract.professional_teken_ip})
                </p>
                <p className="font-mono text-xs mt-1 break-all">sha256 {contract.contract_sha256}</p>
              </div>
            </div>
          ) : !aanDeBeurt ? (
            <p className="text-sm text-gray-600">
              {contract.status === 'WACHT_OP_WERKGEVER'
                ? 'Het contract wacht op de handtekening van de opdrachtgever.'
                : 'Het contract wacht op de tegentekening van de professional.'}
            </p>
          ) : codeVerstuurd === null ? (
            <div className="flex flex-wrap items-center gap-3">
              <FileSignature className="w-5 h-5 text-purple-600" />
              <p className="text-sm text-gray-700 flex-1">
                Onderteken met SMS-verificatie: u ontvangt een unieke code op uw mobiele nummer. Tijdstip en
                IP-adres worden bij de handtekening vastgelegd.
              </p>
              <Button loading={codeMutation.isPending} onClick={() => codeMutation.mutate()}>
                ✍️ {rol === 'werkgever' ? 'Onderteken Contract & Activeer Project' : 'Tegenteken de overeenkomst'}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <Smartphone className="w-5 h-5 text-purple-600 mb-2" />
              <div className="flex-1 min-w-[180px]">
                <Input
                  label={`SMS-code (verstuurd naar ${codeVerstuurd})`}
                  value={code}
                  maxLength={6}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <Button disabled={code.length !== 6} loading={tekenMutation.isPending} onClick={() => tekenMutation.mutate()}>
                Bevestig handtekening
              </Button>
              <Button variant="secondary" size="sm" onClick={() => codeMutation.mutate()} loading={codeMutation.isPending}>
                Nieuwe code
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OvoContractSign;
